import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Button from '../common/Button';
import { Modal, ModalContent, ModalCloseButton } from '../common';
import PlayerSearchInput from '../common/PlayerSearchInput';
import { isSinglesFormat } from '../../utils/competitiveMatchFormat';

const AddScheduledGameModal = ({
  isOpen,
  onClose,
  sessionId,
  clubId,
  playersById,
  sessionPlayerStatus,
  scheduledGames,
  partnerships = [],
  matchFormat = 'doubles',
  onGameScheduled
}) => {
  const singles = isSinglesFormat(matchFormat);
  const [selectedPlayers, setSelectedPlayers] = useState([null, null, null, null]);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setSelectedPlayers(singles ? [null, null] : [null, null, null, null]);
      setErrors([]);
    }
  }, [isOpen, singles]);

  // Get all eligible players (active session members only — matches create_scheduled_game RPC)
  const eligiblePlayers = Object.values(playersById).filter(player => 
    sessionPlayerStatus[player.id] === 'active'
  );

  // Get players already in scheduled games
  const playersInScheduledGames = useMemo(() => {
    const set = new Set();
    scheduledGames.forEach(game => {
      [game.team1_player1_id, game.team1_player2_id, game.team2_player1_id, game.team2_player2_id]
        .filter(Boolean)
        .forEach(id => set.add(id));
    });
    return set;
  }, [scheduledGames]);

  // Filter available players for each search input (exclude already selected players)
  const getAvailablePlayers = (index) => {
    return eligiblePlayers.filter(player => {
      // Don't include players already selected in other slots
      return !selectedPlayers.some((selected, i) => i !== index && selected && selected.id === player.id);
    });
  };

  const handlePlayerSelect = (player, index) => {
    const newSelectedPlayers = [...selectedPlayers];
    newSelectedPlayers[index] = player;
    setSelectedPlayers(newSelectedPlayers);
    // Validation will happen automatically via useEffect
  };

  const handleRemovePlayer = (index) => {
    const newSelectedPlayers = [...selectedPlayers];
    newSelectedPlayers[index] = null;
    setSelectedPlayers(newSelectedPlayers);
    setErrors([]);
  };

  const validateSelection = useCallback(() => {
    const newErrors = [];

    if (singles) {
      if (!selectedPlayers[0] || !selectedPlayers[1]) {
        newErrors.push('Select both players for this singles match');
        setErrors(newErrors);
        return false;
      }
      if (selectedPlayers[0].id === selectedPlayers[1].id) {
        newErrors.push('Cannot select the same player twice');
      }
      if (playersInScheduledGames.has(selectedPlayers[0].id) || playersInScheduledGames.has(selectedPlayers[1].id)) {
        newErrors.push('Some players are already in other scheduled games');
      }
      setErrors(newErrors);
      return newErrors.length === 0;
    }

    // Doubles: all four slots
    if (selectedPlayers.some(player => !player)) {
      newErrors.push('All 4 players must be selected');
      setErrors(newErrors);
      return false;
    }

    const playerIds = selectedPlayers.filter(Boolean).map(p => p.id);
    const uniqueIds = new Set(playerIds);
    if (uniqueIds.size !== playerIds.length) {
      newErrors.push('Cannot select the same player twice');
    }

    const alreadyScheduled = selectedPlayers.filter(player =>
      player && playersInScheduledGames.has(player.id)
    );
    if (alreadyScheduled.length > 0) {
      newErrors.push('Some players are already in other scheduled games');
    }

    if (partnerships && partnerships.length > 0) {
      const team1Ids = [selectedPlayers[0]?.id, selectedPlayers[1]?.id].filter(Boolean);
      const team2Ids = [selectedPlayers[2]?.id, selectedPlayers[3]?.id].filter(Boolean);
      const allSelectedIds = [...team1Ids, ...team2Ids];
      
      for (const partnership of partnerships) {
        const p1 = partnership.player1Id || partnership.player1_id;
        const p2 = partnership.player2Id || partnership.player2_id;
        
        const p1Selected = allSelectedIds.includes(p1);
        const p2Selected = allSelectedIds.includes(p2);
        
        // If both partners are selected, they must be on the same team
        if (p1Selected && p2Selected) {
          const p1InTeam1 = team1Ids.includes(p1);
          const p2InTeam1 = team1Ids.includes(p2);
          const p1InTeam2 = team2Ids.includes(p1);
          const p2InTeam2 = team2Ids.includes(p2);
          
          // Check if partners are on different teams
          if ((p1InTeam1 && p2InTeam2) || (p1InTeam2 && p2InTeam1)) {
            const player1Name = playersById[p1]?.name || 'Unknown';
            const player2Name = playersById[p2]?.name || 'Unknown';
            
            newErrors.push(`Cannot schedule game: ${player1Name} and ${player2Name} are in a partnership and must play on the same team. End the partnership first if you want to schedule this matchup.`);
          }
        }
        
        // NEW: Check if only one partner is selected - they must have their partner on the same team
        if (p1Selected && !p2Selected) {
          const p1InTeam1 = team1Ids.includes(p1);
          const p1InTeam2 = team2Ids.includes(p1);
          const player1Name = playersById[p1]?.name || 'Unknown';
          const player2Name = playersById[p2]?.name || 'Unknown';
          
          // Partner is not selected, so this is a partnership break
          const teamName = p1InTeam1 ? 'Team 1' : 'Team 2';
          newErrors.push(`Partnership violation: ${player1Name} is on ${teamName}, but their partner ${player2Name} is not on the same team. Partners must always play together.`);
        }
        
        if (p2Selected && !p1Selected) {
          const p2InTeam1 = team1Ids.includes(p2);
          const p2InTeam2 = team2Ids.includes(p2);
          const player1Name = playersById[p1]?.name || 'Unknown';
          const player2Name = playersById[p2]?.name || 'Unknown';
          
          // Partner is not selected, so this is a partnership break
          const teamName = p2InTeam1 ? 'Team 1' : 'Team 2';
          newErrors.push(`Partnership violation: ${player2Name} is on ${teamName}, but their partner ${player1Name} is not on the same team. Partners must always play together.`);
        }
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  }, [selectedPlayers, playersInScheduledGames, partnerships, playersById, singles]);

  // Validate selection whenever players change
  useEffect(() => {
    if (isOpen) {
      const allSelected = singles
        ? selectedPlayers[0] && selectedPlayers[1]
        : selectedPlayers.every(player => player !== null);

      if (!singles && partnerships && partnerships.length > 0) {
        const team1Ids = [selectedPlayers[0]?.id, selectedPlayers[1]?.id].filter(Boolean);
        const team2Ids = [selectedPlayers[2]?.id, selectedPlayers[3]?.id].filter(Boolean);
        const allSelectedIds = [...team1Ids, ...team2Ids];
        const partnershipErrors = [];
        
        for (const partnership of partnerships) {
          const p1 = partnership.player1Id || partnership.player1_id;
          const p2 = partnership.player2Id || partnership.player2_id;
          
          const p1Selected = allSelectedIds.includes(p1);
          const p2Selected = allSelectedIds.includes(p2);
          
          // Check if only one partner is selected - this is a partnership break
          if (p1Selected && !p2Selected) {
            const p1InTeam1 = team1Ids.includes(p1);
            const player1Name = playersById[p1]?.name || 'Unknown';
            const player2Name = playersById[p2]?.name || 'Unknown';
            const teamName = p1InTeam1 ? 'Team 1' : 'Team 2';
            partnershipErrors.push(`Partnership violation: ${player1Name} is on ${teamName}, but their partner ${player2Name} is not on the same team. Partners must always play together.`);
          }
          
          if (p2Selected && !p1Selected) {
            const p2InTeam1 = team1Ids.includes(p2);
            const player1Name = playersById[p1]?.name || 'Unknown';
            const player2Name = playersById[p2]?.name || 'Unknown';
            const teamName = p2InTeam1 ? 'Team 1' : 'Team 2';
            partnershipErrors.push(`Partnership violation: ${player2Name} is on ${teamName}, but their partner ${player1Name} is not on the same team. Partners must always play together.`);
          }
        }
        
        if (partnershipErrors.length > 0) {
          setErrors(partnershipErrors);
          return;
        }
      }
      
      // Full validation if all players selected
      if (allSelected) {
        validateSelection();
      } else if (singles || !partnerships?.length) {
        setErrors([]);
      }
    }
  }, [selectedPlayers, isOpen, validateSelection, partnerships, playersById, singles]);

  const handleSchedule = async () => {
    if (!validateSelection()) return;

    const team1 = singles
      ? [selectedPlayers[0].id]
      : [selectedPlayers[0].id, selectedPlayers[1].id];
    const team2 = singles
      ? [selectedPlayers[1].id]
      : [selectedPlayers[2].id, selectedPlayers[3].id];

    try {
      await onGameScheduled(team1, team2);
      // Modal will be closed by parent component
    } catch (error) {
      setErrors([error.message]);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent size="medium" className="overflow-y-auto">
        <div className="flex justify-between items-center mb-6 pr-8">
          <h2 className="text-xl font-bold text-text-primary">Schedule a Game</h2>
          <ModalCloseButton onClose={onClose} />
        </div>

        {errors.length > 0 && (
          <div className="mb-4 p-3 bg-danger-light border border-danger rounded text-danger-dark">
            <ul className="list-disc list-inside">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-6">
          {singles ? (
            <>
              <div className="bg-info-light p-4 rounded-lg border-l-4 border-info">
                <h3 className="font-semibold text-info-dark mb-3">Player 1</h3>
                <PlayerSearchInput
                  placeholder="Search players..."
                  availablePlayers={getAvailablePlayers(0)}
                  selectedPlayer={selectedPlayers[0]}
                  onSelectPlayer={(player) => handlePlayerSelect(player, 0)}
                  onRemovePlayer={() => handleRemovePlayer(0)}
                  showGamesPlayed={true}
                  showStatus={true}
                  showSelectedInfo={false}
                  playersInScheduledGames={playersInScheduledGames}
                  className="w-full"
                />
              </div>
              <div className="bg-surface-subtle p-4 rounded-lg border-l-4 border-primary">
                <h3 className="font-semibold text-primary mb-3">Player 2</h3>
                <PlayerSearchInput
                  placeholder="Search players..."
                  availablePlayers={getAvailablePlayers(1)}
                  selectedPlayer={selectedPlayers[1]}
                  onSelectPlayer={(player) => handlePlayerSelect(player, 1)}
                  onRemovePlayer={() => handleRemovePlayer(1)}
                  showGamesPlayed={true}
                  showStatus={true}
                  showSelectedInfo={false}
                  playersInScheduledGames={playersInScheduledGames}
                  className="w-full"
                />
              </div>
            </>
          ) : (
            <>
              <div className="bg-info-light p-4 rounded-lg border-l-4 border-info">
                <h3 className="font-semibold text-info-dark mb-3">Team 1</h3>
                <div className="grid grid-cols-2 gap-4">
                  {[0, 1].map(index => (
                    <div key={index}>
                      <label className="block text-sm font-medium text-text-secondary mb-1">
                        Player {index + 1}
                      </label>
                      <PlayerSearchInput
                        placeholder="Search players..."
                        availablePlayers={getAvailablePlayers(index)}
                        selectedPlayer={selectedPlayers[index]}
                        onSelectPlayer={(player) => handlePlayerSelect(player, index)}
                        onRemovePlayer={() => handleRemovePlayer(index)}
                        showGamesPlayed={true}
                        showStatus={true}
                        showSelectedInfo={false}
                        playersInScheduledGames={playersInScheduledGames}
                        className="w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-surface-subtle p-4 rounded-lg border-l-4 border-primary">
                <h3 className="font-semibold text-primary mb-3">Team 2</h3>
                <div className="grid grid-cols-2 gap-4">
                  {[2, 3].map(index => (
                    <div key={index}>
                      <label className="block text-sm font-medium text-text-secondary mb-1">
                        Player {index - 1}
                      </label>
                      <PlayerSearchInput
                        placeholder="Search players..."
                        availablePlayers={getAvailablePlayers(index)}
                        selectedPlayer={selectedPlayers[index]}
                        onSelectPlayer={(player) => handlePlayerSelect(player, index)}
                        onRemovePlayer={() => handleRemovePlayer(index)}
                        showGamesPlayed={true}
                        showStatus={true}
                        showSelectedInfo={false}
                        playersInScheduledGames={playersInScheduledGames}
                        className="w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            onClick={onClose}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSchedule}
            disabled={
              (singles ? !selectedPlayers[0] || !selectedPlayers[1] : selectedPlayers.some((p) => !p)) ||
              errors.length > 0
            }
            variant="primary"
          >
            Schedule Game
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default AddScheduledGameModal;
